import {
  CheckCircle2,
  CircleCheck,
  History,
  ListTodo,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { MilestoneHistoryDialog } from "@/components/projects/MilestoneHistoryDialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Milestone, MilestonePhase } from "@/hooks/use-milestones";
import {
  useCompleteMilestone,
  useCreateMilestone,
  useDeleteMilestone,
  useInstantiateDefaultMilestones,
  useProjectMilestones,
  useReopenMilestone,
  useUpdateMilestone,
} from "@/hooks/use-milestones";

const PHASE_ORDER: MilestonePhase[] = ["pre_construction", "construction", "post_construction"];

const PHASE_LABELS: Record<MilestonePhase, string> = {
  pre_construction: "Pre-Construction",
  construction: "Construction",
  post_construction: "Post-Construction",
};

function todayISO(): string {
  const iso = new Date().toISOString().split("T")[0];
  return iso ?? "";
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

type RowStatus = "on_time" | "completed_late" | "at_risk" | "open";

function computeRowStatus(m: Milestone): RowStatus {
  if (m.actualDate) {
    return m.contractDate && m.actualDate > m.contractDate ? "completed_late" : "on_time";
  }
  if (m.forecastDate && m.contractDate && m.forecastDate > m.contractDate) return "at_risk";
  return "open";
}

function StatusPill({ status }: { status: RowStatus }) {
  if (status === "on_time") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
        <CircleCheck className="h-3 w-3" />
        Complete
      </span>
    );
  }
  if (status === "completed_late") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
        <CircleCheck className="h-3 w-3" />
        Complete (Late)
      </span>
    );
  }
  if (status === "at_risk") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
        At Risk
      </span>
    );
  }
  return <span className="text-xs text-gray-400">—</span>;
}

function TemplateBadge({ name }: { name: string | null }) {
  return (
    <span
      className="ml-2 inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
      title={name ?? "Template-derived"}
    >
      Template
    </span>
  );
}

function sortMilestones(rows: Milestone[]): Milestone[] {
  return [...rows].sort((a, b) => {
    if (a.contractDate && b.contractDate) {
      if (a.contractDate !== b.contractDate) return a.contractDate.localeCompare(b.contractDate);
    } else if (a.contractDate) {
      return -1;
    } else if (b.contractDate) {
      return 1;
    }
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });
}

function groupByPhase(rows: Milestone[]): Record<MilestonePhase, Milestone[]> {
  const acc: Record<MilestonePhase, Milestone[]> = {
    pre_construction: [],
    construction: [],
    post_construction: [],
  };
  for (const r of rows) {
    const phase = (r.phase ?? "construction") as MilestonePhase;
    acc[phase].push(r);
  }
  for (const key of Object.keys(acc) as MilestonePhase[]) {
    acc[key] = sortMilestones(acc[key]);
  }
  return acc;
}

function loadExpandedPhases(projectId: string): MilestonePhase[] {
  if (typeof window === "undefined") return PHASE_ORDER;
  try {
    const raw = window.sessionStorage.getItem(`pbos.milestones.expandedPhases.${projectId}`);
    if (!raw) return PHASE_ORDER;
    const parsed = JSON.parse(raw) as string[];
    return parsed.filter((p): p is MilestonePhase => PHASE_ORDER.includes(p as MilestonePhase));
  } catch {
    return PHASE_ORDER;
  }
}

function saveExpandedPhases(projectId: string, values: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      `pbos.milestones.expandedPhases.${projectId}`,
      JSON.stringify(values),
    );
  } catch {
    // ignore quota errors
  }
}

// ── Inline date editor ──

function InlineDateEditor({
  value,
  disabled,
  onCommit,
  ariaLabel,
}: {
  value: string | null;
  disabled?: boolean;
  onCommit: (next: string | null) => void;
  ariaLabel?: string;
}) {
  if (disabled) {
    return <span className="text-sm text-gray-700">{formatDate(value)}</span>;
  }

  return (
    <DatePicker
      value={value}
      onChange={(next) => {
        if (next !== value) onCommit(next);
      }}
      placeholder="—"
      size="sm"
      ariaLabel={ariaLabel}
    />
  );
}

// ── Mark-complete dialog ──

function MarkCompleteDialog({
  open,
  onOpenChange,
  milestoneName,
  onConfirm,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  milestoneName: string;
  onConfirm: (actualDate: string) => void;
  loading: boolean;
}) {
  const today = todayISO();
  const [date, setDate] = useState(today);

  useEffect(() => {
    if (open) setDate(today);
  }, [open, today]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-[#334155]">
            Mark milestone complete
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Set the actual completion date for{" "}
            <span className="font-medium text-gray-900">{milestoneName}</span>. You can record a
            past date if the milestone was completed earlier.
          </p>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-gray-700">Actual Date</Label>
            <DatePicker
              value={date}
              onChange={(v) => setDate(v ?? "")}
              max={today}
              clearable={false}
              ariaLabel="Actual completion date"
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={loading || !date}
            onClick={() => onConfirm(date)}
            className="bg-[#334155] hover:bg-[#415A77] text-white"
          >
            {loading ? "Saving..." : "Mark Complete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Add milestone dialog (inline, one-off additions) ──

function AddMilestoneDialog({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createMilestone = useCreateMilestone(projectId);
  const [name, setName] = useState("");
  const [phase, setPhase] = useState<MilestonePhase>("construction");
  const [contractDate, setContractDate] = useState("");
  const [forecastDate, setForecastDate] = useState("");

  function reset() {
    setName("");
    setPhase("construction");
    setContractDate("");
    setForecastDate("");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMilestone.mutate(
      {
        name,
        phase,
        contractDate: contractDate || null,
        forecastDate: forecastDate || null,
      },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-[#334155]">Add Milestone</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="add-m-name" className="text-sm font-medium text-gray-700">
              Name
            </Label>
            <Input
              id="add-m-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-m-phase" className="text-sm font-medium text-gray-700">
              Phase
            </Label>
            <Select value={phase} onValueChange={(v) => setPhase(v as MilestonePhase)}>
              <SelectTrigger id="add-m-phase">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pre_construction">Pre-Construction</SelectItem>
                <SelectItem value="construction">Construction</SelectItem>
                <SelectItem value="post_construction">Post-Construction</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Contract Date</Label>
              <DatePicker
                value={contractDate || null}
                onChange={(v) => setContractDate(v ?? "")}
                ariaLabel="Contract date"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Forecast Date</Label>
              <DatePicker
                value={forecastDate || null}
                onChange={(v) => setForecastDate(v ?? "")}
                ariaLabel="Forecast date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMilestone.isPending}
              className="bg-[#334155] hover:bg-[#415A77] text-white"
            >
              {createMilestone.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ──

interface MilestonesTabProps {
  projectId: string;
}

export function MilestonesTab({ projectId }: MilestonesTabProps) {
  const { data, isLoading, isError, error, refetch } = useProjectMilestones(projectId);
  const updateMilestone = useUpdateMilestone(projectId);
  const completeMilestone = useCompleteMilestone(projectId);
  const reopenMilestone = useReopenMilestone(projectId);
  const deleteMilestone = useDeleteMilestone(projectId);
  const instantiateDefaults = useInstantiateDefaultMilestones(projectId);

  const [expanded, setExpanded] = useState<string[]>(() => loadExpandedPhases(projectId));
  const [addOpen, setAddOpen] = useState(false);
  const [addDefaultsConfirm, setAddDefaultsConfirm] = useState(false);

  const [completeTarget, setCompleteTarget] = useState<Milestone | null>(null);
  const [reopenTarget, setReopenTarget] = useState<Milestone | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Milestone | null>(null);
  const [historyTarget, setHistoryTarget] = useState<Milestone | null>(null);

  useEffect(() => {
    setExpanded(loadExpandedPhases(projectId));
  }, [projectId]);

  const grouped = useMemo(() => groupByPhase(data ?? []), [data]);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => refetch()} />;

  const items = data ?? [];

  const handleAddDefaultsClick = () => {
    if (items.length > 0) {
      setAddDefaultsConfirm(true);
    } else {
      instantiateDefaults.mutate();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddDefaultsClick}
          disabled={instantiateDefaults.isPending}
          className="rounded-lg"
        >
          <Sparkles className="mr-2 h-4 w-4" />
          {instantiateDefaults.isPending ? "Adding..." : "Add Defaults"}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="rounded-lg">
          <Plus className="mr-2 h-4 w-4" />
          Add Milestone
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <ListTodo className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 mb-4">No milestones yet for this project.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => instantiateDefaults.mutate()}
            disabled={instantiateDefaults.isPending}
            className="rounded-lg"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {instantiateDefaults.isPending ? "Adding..." : "Add Defaults"}
          </Button>
        </div>
      ) : (
        <Accordion
          type="multiple"
          value={expanded}
          onValueChange={(next) => {
            setExpanded(next);
            saveExpandedPhases(projectId, next);
          }}
          className="space-y-4"
        >
          {PHASE_ORDER.map((phase) => {
            const rows = grouped[phase];
            const completeCount = rows.filter((r) => !!r.actualDate).length;
            return (
              <AccordionItem
                key={phase}
                value={phase}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
              >
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50">
                  <div className="flex flex-1 items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {PHASE_LABELS[phase]}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({rows.length} milestone{rows.length === 1 ? "" : "s"})
                      </span>
                    </div>
                    <span className="text-xs font-medium text-gray-500">
                      {completeCount}/{rows.length} complete
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="p-0">
                  {rows.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-gray-500">
                      No milestones in this phase.
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                            Milestone
                          </TableHead>
                          <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            Contract Date
                          </TableHead>
                          <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            Forecast Date
                          </TableHead>
                          <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            Actual Date
                          </TableHead>
                          <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            Status
                          </TableHead>
                          <TableHead className="text-right pr-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                            <span className="sr-only">Actions</span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((m) => {
                          const status = computeRowStatus(m);
                          const isComplete = status === "on_time" || status === "completed_late";
                          const rowClass =
                            status === "completed_late"
                              ? "bg-red-50/50"
                              : status === "at_risk"
                                ? "bg-amber-50/40"
                                : undefined;
                          return (
                            <TableRow key={m.id} className={rowClass}>
                              <TableCell className="px-4 py-3 align-middle">
                                <div className="flex items-center">
                                  <span
                                    className={`text-sm font-medium ${
                                      isComplete ? "text-gray-500" : "text-gray-900"
                                    }`}
                                  >
                                    {m.name}
                                  </span>
                                  {m.isTemplateDerived && <TemplateBadge name={m.templateName} />}
                                </div>
                              </TableCell>
                              <TableCell className="py-3">
                                <InlineDateEditor
                                  value={m.contractDate}
                                  disabled={isComplete}
                                  ariaLabel={`Contract date for ${m.name}`}
                                  onCommit={(next) =>
                                    updateMilestone.mutate({
                                      milestoneId: m.id,
                                      contractDate: next,
                                    })
                                  }
                                />
                              </TableCell>
                              <TableCell className="py-3">
                                <InlineDateEditor
                                  value={m.forecastDate}
                                  disabled={isComplete}
                                  ariaLabel={`Forecast date for ${m.name}`}
                                  onCommit={(next) =>
                                    updateMilestone.mutate({
                                      milestoneId: m.id,
                                      forecastDate: next,
                                    })
                                  }
                                />
                              </TableCell>
                              <TableCell className="py-3 text-sm text-gray-700">
                                {formatDate(m.actualDate)}
                              </TableCell>
                              <TableCell className="py-3">
                                <StatusPill status={status} />
                              </TableCell>
                              <TableCell className="py-3 pr-4 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-gray-400 hover:text-[#334155]"
                                    onClick={() => setHistoryTarget(m)}
                                    title="History"
                                  >
                                    <History className="h-3.5 w-3.5" />
                                  </Button>
                                  {isComplete ? (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-gray-400 hover:text-[#334155]"
                                      onClick={() => setReopenTarget(m)}
                                      title="Reopen"
                                    >
                                      <RotateCcw className="h-3.5 w-3.5" />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-gray-400 hover:text-emerald-600"
                                      onClick={() => setCompleteTarget(m)}
                                      title="Mark complete"
                                    >
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                    onClick={() => setDeleteTarget(m)}
                                    title="Delete"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <AddMilestoneDialog projectId={projectId} open={addOpen} onOpenChange={setAddOpen} />

      <MarkCompleteDialog
        open={completeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCompleteTarget(null);
        }}
        milestoneName={completeTarget?.name ?? ""}
        loading={completeMilestone.isPending}
        onConfirm={(actualDate) => {
          if (!completeTarget) return;
          completeMilestone.mutate(
            { milestoneId: completeTarget.id, actualDate },
            {
              onSuccess: () => setCompleteTarget(null),
            },
          );
        }}
      />

      <ConfirmDialog
        open={reopenTarget !== null}
        onOpenChange={(open) => {
          if (!open) setReopenTarget(null);
        }}
        title="Reopen milestone"
        description="This will clear the Actual Date and return the milestone to active. The change will be recorded in the audit history."
        confirmLabel="Reopen"
        onConfirm={() => {
          if (!reopenTarget) return;
          reopenMilestone.mutate(reopenTarget.id);
          setReopenTarget(null);
        }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete milestone"
        description={`Remove "${deleteTarget?.name ?? ""}" from this project? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMilestone.mutate(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />

      <ConfirmDialog
        open={addDefaultsConfirm}
        onOpenChange={setAddDefaultsConfirm}
        title="Add default milestones?"
        description="This project already has milestones. Adding defaults will create additional milestone entries and may result in duplicates. Continue?"
        confirmLabel="Add Defaults"
        onConfirm={() => {
          instantiateDefaults.mutate();
          setAddDefaultsConfirm(false);
        }}
      />

      {historyTarget && (
        <MilestoneHistoryDialog
          projectId={projectId}
          milestoneId={historyTarget.id}
          milestoneName={historyTarget.name}
          open={historyTarget !== null}
          onOpenChange={(open) => {
            if (!open) setHistoryTarget(null);
          }}
        />
      )}
    </div>
  );
}
